import {makeAutoObservable} from 'mobx';
import s from '../../../s';
import {modal} from '../../../../util';
import type {EPTemplateOption} from '../ep-template/ep-templates';
import {applyTemplate, clearTemplate, setTemplateLine, setTemplatePlateSize} from '../ep-template/ep-template-apply';
import {gateMessage, INST_PLATE_DEFAULT_SIZE, InstPlateSize, InstPlateType, InstPlateTypeOption, instPlateSizesFor, PlateSize, plateTypeOfTemplate, plateTypeSetFor, sameSize, selectionAfterChange, templatesFor} from './inst-plate-rules';

/** The module's collapsible body (mobile), opened when the add-to-cart gate blocks so the shopper sees what is missing. */
export const INST_PLATE_INNER_ID = 'inst-plate-inner';

/** The product page's "Personalization Required" modal (product.tsx), shared with the template-line check in api.ts. */
export const INST_PLATE_ERROR_MODAL_ID = 'modal-ep-template-error';

/** What an Add to Cart click does for an inst-plate product: stop at the gate, show the proof, or go to the cart. */
export type InstPlateAtcStep = 'blocked' | 'confirm' | 'add';

/**
 * UI state of the personalized-plate module (INST-PLATES D6). The plate itself stays in the
 * product state: the template is s.ps.EPTemplateID, the size s.ps.EPWidth x EPHeight, the
 * lines ep1..ep10 and the resolved HTML EPContent, so `templateId` and `size` read them. The
 * store keeps what the product state has no field for: the chosen plate type, the
 * certification box (`approved`), the proof confirmation (`confirmed`), the error display
 * and the confirmation modal.
 */
class InstPlateStore {
    // The type the shopper picked; null until then, when the selected template's own type (a
    // restored cart line) or the family's default (With Name; Without License for the profession
    // templates) applies. Pinned on every change so clearing a template never flips the type shown.
    chosenType: InstPlateType | null = null;
    approved = false;
    confirmed = false;
    showErrors = false;
    confirmOpen = false;
    pendingBuyNow = false;
    // The plate finish (s.ps.ep_id) the proof showed when the shopper confirmed it: choosing another insignia
    // afterwards changes the finish (gold or silver), so the next Add to Cart shows the proof again.
    confirmedFinish: number | undefined = undefined;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    get templates(): EPTemplateOption[] {
        return s.prod?.EPTemplates || [];
    }

    get templateId(): number {
        return s.ps && s.ps.EPTemplateID > 0 ? s.ps.EPTemplateID : -1;
    }

    get selectedTemplate(): EPTemplateOption | undefined {
        return this.templateId > 0 ? this.templates.find(t => t.id === this.templateId) : undefined;
    }

    get size(): PlateSize {
        return {width: s.ps?.EPWidth || INST_PLATE_DEFAULT_SIZE.width, height: s.ps?.EPHeight || INST_PLATE_DEFAULT_SIZE.height};
    }

    get plateType(): InstPlateType {
        return this.chosenType ?? plateTypeOfTemplate(this.selectedTemplate) ?? plateTypeSetFor(this.templates).defaultType;
    }

    /** The "Plate Type" buttons: the family of the product's templates (With Name / Without Name, or With License / Without License). */
    get plateTypes(): readonly InstPlateTypeOption[] {
        return plateTypeSetFor(this.templates).types;
    }

    /** The size tiles: the module sizes the product's typed templates come in. */
    get sizes(): readonly InstPlateSize[] {
        return instPlateSizesFor(this.templates);
    }

    /** The "Select a Template" options for the current size and type. */
    get available(): EPTemplateOption[] {
        return templatesFor(this.templates, this.size, this.plateType);
    }

    get gateMessage(): string | null {
        return gateMessage({templateId: this.templateId, missingLineTitles: s.epTemplateMissingLineTitles || [], approved: this.approved});
    }

    resetAcknowledgment() {
        this.approved = false;
        this.confirmed = false;
    }

    private rerender() {
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }

    /** A size tile: the plate takes the size on the frame and in the preview; a template of another size is cleared. */
    setSize(size: PlateSize) {
        if (sameSize(size, this.size)) return;

        this.chosenType = this.plateType;
        this.resetAcknowledgment();
        setTemplatePlateSize(size.width, size.height);
        if (selectionAfterChange(this.templates, {size, plateType: this.plateType, templateId: this.templateId}).cleared) {
            clearTemplate();
        }
        this.rerender();
    }

    /** A Plate Type button: a template that is not listed under the new type is cleared ("any" templates stay). */
    setPlateType(plateType: InstPlateType) {
        if (plateType === this.plateType) {
            this.chosenType = plateType;
            return;
        }

        this.chosenType = plateType;
        this.resetAcknowledgment();
        if (selectionAfterChange(this.templates, {size: this.size, plateType, templateId: this.templateId}).cleared) {
            clearTemplate();
        }
        this.rerender();
    }

    /** The template dropdown: apply a listed template (lines start over with their defaults), or none. */
    selectTemplate(id: number) {
        if (id === this.templateId) return;

        this.chosenType = this.plateType;
        this.resetAcknowledgment();
        const template = id > 0 ? this.available.find(t => t.id === id) : undefined;
        if (template) {
            applyTemplate(template);
            return;
        }
        clearTemplate();
        this.rerender();
    }

    /** A line input. */
    setLine(lineNumber: number, value: string) {
        this.resetAcknowledgment();
        setTemplateLine(lineNumber, value);
    }

    /** The certification box; unticking it also withdraws a confirmation. */
    setApproved(approved: boolean) {
        this.approved = approved;
        if (!approved) {
            this.confirmed = false;
        }
    }

    /**
     * Run by every Add to Cart button of an inst-plate product once its other checks pass.
     * An incomplete plate (no template, a blank or invalid line, the box unticked) opens the
     * "Personalization Required" modal and highlights what is missing; a complete one that
     * is not confirmed yet opens the proof; a confirmed one goes to the cart.
     */
    beforeAddToCart(buyNow = false): InstPlateAtcStep {
        if (this.gateMessage) {
            this.showErrors = true;
            s.ui.ep_template_show_errors = true;
            this.confirmOpen = false;
            if (typeof document !== 'undefined') {
                document.getElementById(INST_PLATE_INNER_ID)?.classList.add('show');
            }
            modal(INST_PLATE_ERROR_MODAL_ID);
            return 'blocked';
        }

        this.showErrors = false;
        if (this.confirmed && s.ps?.ep_id !== this.confirmedFinish) {
            this.confirmed = false;
        }
        if (!this.confirmed) {
            this.pendingBuyNow = buyNow;
            this.confirmOpen = true;
            return 'confirm';
        }
        return 'add';
    }

    /** The proof's close button, backdrop or Escape: back to the page, nothing withdrawn. */
    closeConfirm() {
        this.confirmOpen = false;
    }

    /** "Edit Personalization": back to the module with the box unticked. */
    editPersonalization() {
        this.confirmOpen = false;
        this.resetAcknowledgment();
    }

    /** "Confirm & Add to Cart": the proof is confirmed; returns the click's buy-now flag for add_to_cart. */
    confirm(): boolean {
        this.confirmed = true;
        this.confirmedFinish = s.ps?.ep_id;
        this.confirmOpen = false;
        return this.pendingBuyNow;
    }
}

export const instPlateStore = new InstPlateStore();
